import type { GetDesignTypesResponse } from "@nlc/shared-types";

import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { designTypesKeys } from "@/hooks/queryKeys";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";
import apiClient from "@/lib/api/client";

type UseApplyDefaultComplexityProps = {
  applyDefault: (complexityId: number) => void;
  designTypeId: null | number | undefined;
  hasComplexity: () => boolean;
};

/**
 * Sets the level to the design type's default whenever the design type changes.
 * A level that arrives together with the first design type (draft or duplicate)
 * is kept.
 */
export function useApplyDefaultComplexity({
  applyDefault,
  designTypeId,
  hasComplexity,
}: UseApplyDefaultComplexityProps) {
  const projectId = useGetCurrentProjectId();
  const { data: defaultComplexityIds } = useSuspenseQuery<
    GetDesignTypesResponse,
    Error,
    Map<number, null | number>
  >({
    queryFn: async () =>
      await apiClient.url(`/design-types/picker/${projectId}`).get().json(),
    queryKey: designTypesKeys.designTypesByProjectId(projectId),
    select: (data) =>
      new Map(
        data.designTypes.types.map((type) => [
          type.id,
          type.defaultComplexityId,
        ]),
      ),
  });

  const previousDesignTypeId = useRef<null | number | undefined>();

  useEffect(() => {
    if (!designTypeId || previousDesignTypeId.current === designTypeId) return;

    const isFirstDesignType = previousDesignTypeId.current === undefined;

    previousDesignTypeId.current = designTypeId;

    if (isFirstDesignType && hasComplexity()) return;

    const defaultComplexityId = defaultComplexityIds.get(designTypeId);

    if (defaultComplexityId) applyDefault(defaultComplexityId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designTypeId]);
}
